// The right column — the inputs, station by station, then the model.
//
// Cody, 2026-10-04: "We show all data going into the model — then the model.
// Chart every station and its models, then show our prediction: by doing it
// this way it shows how reasonable we are." And: "the honest version is showing
// that this ISN'T the simple model — they already have the dumb model, X buoy
// minus X degrees."
//
//   1 NDBC 46215            the water at each station: its own readings, heavy,
//   2 Port San Luis         and beside them the model that stands in for the
//   3 NDBC 46218 (Harvest)  station in our inputs (Open-Meteo at that site, on
//                           that station's own scale; 46215 also HYCOM and
//                           CMEMS). The reader sees the models track the water
//                           before seeing anything built from them.
//   4 Intake                the head-to-head: measured daily max, SeaState,
//                           PG&E's offset model, the 64 °F rule.
//
// One line, one colour, the same colour everywhere (legend.ts). No fills.
// Sixty days back so the reader can see who was right, nine forward so they
// can see what each says next. One window, one gutter, one ruler, one cursor.
//
// THE LABEL. Past days are scored on the archived ocean analysis, not on
// forecasts issued at the time — no source keeps sea temperature as issued
// (checked 2026-10-04 with the commercial key; research/archive/ accrues it
// from today). Both methods get the same water; the intake track's source
// line says so and the readout title says "on the analysis" per day.
//
// What left and why: the three-model envelope (a fill a reader could not tell
// from a band), the confidence strip (its bands are two to four days wide on a
// sixty-day axis and their labels did not fit; the Skill tab carries the
// numbers), the raw "Sources at the buoy" track (a different quantity under
// a chart of the intake, which read as the same models at two temperatures).

import { createChart, type Chart, type ChartConfig } from "../../chart/chart.js";
import { nowLine, rgba } from "../../chart/marks.js";
import { createTempChart, type TempChart, type TempPoint } from "./tempchart.js";
import { attachReadout, renderReadout } from "../../chart/readout.js";
import { createTimeRuler, type TimeRuler } from "../../chart/ruler.js";
import { trackBlock, mountChart, bindMeter, setCollapseNamespace, type Block }
  from "../shell/track.js";
import { createTransport, type Transport } from "../../chart/transport.js";
import { setLabelZone, getCursor, subscribeCursor, indexAtTime } from "../../time/cursor.js";
import { type Payload, type Day, num, spreadAt, memberCount } from "./payload.js";
import {
  resolveColours, stationSeries, headToHeadReadout, dayTitle, lastIndex, type Colours,
} from "./legend.js";

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Major-tick label for a DATE-ONLY axis: "2026-07-29" -> "Wed 29 Jul". The
 * month is not decoration: a sixty-day axis runs across three of them, and
 * "Wed 5" meant August at one end and nothing at all at the other.
 *
 * THE RULER DREW NOTHING BUT THE CURSOR WITHOUT THIS. `createTimeRuler`'s
 * default major tick asks "is this label local midnight?" — built for the fleet
 * console's HOURLY axis. A date-only label parsed as UTC midnight is 16:00 or
 * 17:00 the day before in site time, so the test failed on every label. On a
 * daily axis every label IS a day; the ruler's own gap measurement thins them.
 */
export function dayTick(label: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (m === null) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

export interface Tracks {
  /** Re-grade against a new threshold: the intake track's rule line moves. */
  setThreshold(f: number): void;
  destroy(): void;
  /** For tests: how many tracks actually carry data. */
  drawnSeries(): number;
}

// 46, matching main.ts's STACK_GUTTER: one x means one date on every track.
const GUTTER = 46;

// THE WINDOW: a week back, a now line, nine days forward — the forecast is more
// than half the width. Cody: "why so much history? We need the near-term future
// to take up more space, that's what they care about." The sixty-day
// head-to-head still exists as a number: the Site panel's scorecard is computed
// over the whole payload, not over this window.
const PAST_DAYS = 7;

/** The stations, in the order they are stacked: the cove buoy at the intake,
 *  the bay station, the offshore buoy. The intake is not here; it is the model.
 *  Titles are short enough for the 150 px track header; the station code and
 *  the distance go in the subtitle. */
const STATION_ORDER = ["46215", "pslc1", "46218"] as const;
const STATION_TITLE: Record<(typeof STATION_ORDER)[number], string> = {
  "46215": "Buoy 46215", pslc1: "Port San Luis", "46218": "Harvest buoy",
};
const STATION_CODE: Record<(typeof STATION_ORDER)[number], string> = {
  "46215": "NDBC 46215", pslc1: "CeNCOOS PSLC1", "46218": "NDBC 46218",
};

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

/** A y range that fits the data; chart.ts defaults yMin to 0, which is wrong
 *  for sea temperature. */
function yRange(data: (number | null)[][]): { yMin: number; yMax: number } {
  const vals = data.flat().filter((v): v is number => v !== null && Number.isFinite(v));
  if (vals.length === 0) return { yMin: 0, yMax: 1 };
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const pad = Math.max((hi - lo) * 0.1, 0.5);
  return { yMin: lo - pad, yMax: hi + pad };
}

/** Trim to [today − PAST_DAYS, end] and report where "now" lands in the result. */
function windowed<T>(dates: string[], rows: T[], today: string): {
  dates: string[]; rows: T[]; nowIndex: number;
} {
  const cut = new Date(Date.parse(`${today}T00:00:00Z`) - PAST_DAYS * 86400000)
    .toISOString()
    .slice(0, 10);
  const keep: number[] = [];
  dates.forEach((d, i) => {
    if (d >= cut) keep.push(i);
  });
  const outDates = keep.map((i) => dates[i] as string);
  return {
    dates: outDates,
    rows: keep.map((i) => rows[i] as T),
    nowIndex: outDates.reduce((acc, d, i) => (d <= today ? i : acc), -1),
  };
}

export function createTracks(host: HTMLElement, p: Payload): Tracks {
  setCollapseNamespace("thermal");
  // PST calendar days: parse the labels in PST so the playhead lands on the
  // right day from any browser zone.
  setLabelZone(-8 * 3600);

  const stack = document.createElement("div");
  stack.className = "trackstack";
  host.appendChild(stack);
  const transport: Transport = createTransport(stack);
  const rulerRow = document.createElement("div");
  rulerRow.className = "rulerrow";
  const stub = document.createElement("span");
  stub.className = "stubnm";
  stub.textContent = "Timeline";
  const rulerCanvas = document.createElement("canvas");
  rulerRow.append(stub, rulerCanvas);
  stack.appendChild(rulerRow);

  let ruler: TimeRuler | null = null;
  host = stack;
  const series: Day[] = Array.isArray(p.series) ? p.series : [];
  const today = p.today ?? "";
  let threshold = p.thresholds?.action_f ?? 64;
  const blocks: Block[] = [];
  const teardowns: Array<() => void> = [];
  let temp: TempChart | null = null;
  let drawn = 0;
  const colours: Colours = resolveColours(cssVar);
  // The range swatch is the band's own amber at the band's own tint, so the
  // legend shows the shape the chart draws.
  colours.range = rgba(cssVar("--warn", "#b46a00"), 0.45);

  const labels = series.map((d) => d.date ?? "");
  // Every track is cut to this one window and indexed on these dates.
  const tw = windowed(labels, series, today);

  // --- the stations, each with the model that stands in for it ---------------
  // Built first, shown second: the intake block below is moved above them.
  const stationCharts: { chart: Chart; rebuild: () => ChartConfig["series"] }[] = [];
  const sources = Array.isArray(p.sources) ? p.sources : [];
  for (const sk of STATION_ORDER) {
    const models = p.station_models?.[sk];
    const build = (): ChartConfig["series"] => stationSeries(models, tw.dates, colours);
    const ser = build();
    if (ser.length === 0) continue;
    const src = sources.find((s) => s.id === sk);
    const km = typeof src?.km === "number" ? ` · ${src.km.toFixed(1)} km from the intake` : "";
    // The legend above the plot names every line; the subtitle says what the
    // quantity is and where the station sits.
    const b = trackBlock(host, STATION_TITLE[sk], `${STATION_CODE[sk]} · daily minimum °F${km}`);
    b.root.classList.add("t-station");
    blocks.push(b);
    const cfgS: ChartConfig = {
      series: ser,
      xLabels: tw.dates,
      yUnit: "°F",
      ...yRange(ser.map((s) => s.data)),
      gutterLeft: GUTTER,
      xAxis: false, // one ruler, above
      // No threshold here: 64 °F is an intake limit, and this is water at a
      // station. The limit is drawn where it applies, on the intake track.
      marks: [nowLine(tw.nowIndex)],
      legend: false, // the readout strip above the plot is the legend, with values
      tooltipTitle: (i) => dayTitle(tw.dates[i] ?? ""),
    };
    const chart = mountChart(b, (c) => createChart(c, cfgS));
    const fit = models?.openmeteo_fit;
    b.src.textContent =
      `Open-Meteo marine (Météo-France SMOC) at this site, shifted onto this station's own ` +
      `readings` +
      (fit?.n !== undefined ? ` (fit before the window: ${fit.n} d, ±${fit.resid_sd_f?.toFixed(2)} °F)` : "") +
      ` · past: analysis · forward: forecast` +
      (models?.hycom !== undefined || models?.cmems !== undefined
        ? " · HYCOM and CMEMS likewise, where their runs reach" : "");
    const measured = ser.find((s) => s.label === "Measured");
    if (measured !== undefined) {
      teardowns.push(bindMeter(b, tw.dates, measured.data, "°F", 1, () => null,
        lastIndex(measured.data)));
    }
    if (chart !== null) {
      b.readout.hidden = false;
      teardowns.push(attachReadout(chart, b.readout));
      stationCharts.push({ chart, rebuild: build });
    }
    drawn++;
  }

  // --- the intake: the head-to-head — FIRST in the stack ----------------------
  // It is built after the stations because it reads their colours and window,
  // but it is the prediction, and the prediction goes at the top (Les,
  // 2026-10-09). The stations are the evidence under it, in the order they sit
  // from the cove outward. Inserted after the ruler row, which leads the stack.
  const tempB = trackBlock(host, "Intake",
    "daily maximum °F · measured · SeaState · Open-Meteo + offset · 3-model range");
  host.insertBefore(tempB.root, rulerRow.nextSibling);
  tempB.root.classList.add("t-outlook");
  blocks.push(tempB);
  const tempCanvas = document.createElement("canvas");
  tempB.wrap.replaceChildren(tempCanvas);
  const hh = p.head_to_head;
  const points: TempPoint[] = tw.rows.map((d) => {
    // THE RANGE: the three-model envelope on the intake scale, the same row the
    // verdict tile and the grid read, on every day the payload has it.
    const sp = spreadAt(p, d.date);
    return {
      date: d.date ?? "",
      measured: num(d.measured_f),
      measuredPartial: num(d.measured_partial_f),
      measuredTo: d.measured_to ?? null,
      measuredHours: typeof d.measured_hours === "number" ? d.measured_hours : null,
      hindcast: num(d.hindcast_f),
      estimate: num(d.estimate_f),
      forecast: num(d.forecast_f),
      pge: num(d.pge_f),
      rangeLo: num(sp?.lo_f),
      rangeHi: num(sp?.hi_f),
      members: sp === undefined ? null : memberCount(sp),
    };
  });
  const nMeasured = points.filter((pt) => pt.measured !== null).length;
  const nHindcast = points.filter((pt) => pt.measured !== null && pt.hindcast !== null).length;
  tempB.src.textContent =
    (hh?.basis ?? "Past days on the archived analysis; forward days on today's forecast.") +
    ` "Open-Meteo + offset" is OUR RECONSTRUCTION of PG&E's incumbent from Les Bender's ` +
    `description (3 Oct 2026: "a simple offset model using the Open-Meteo forecasts"): ` +
    `Open-Meteo at the buoy plus a seasonal offset fitted on prior data. Their actual offset ` +
    `and inputs are not known to us. SeaState fitted on data before ` +
    `${hh?.window_from ?? "the window"} and drawn only on days every station input was ` +
    `reported (${nHindcast} of ${nMeasured} measured days). This compares the two mappings ` +
    `onto the intake, not forecast skill; the as-issued record accrues from today. ` +
    `Amber band: the three-model range — the ocean models above, each shifted onto the ` +
    `intake — the same numbers as the grid's range rows.`;
  const rules = (): { value: number; label: string; kind: "action" | "ceiling" }[] => {
    const out: { value: number; label: string; kind: "action" | "ceiling" }[] = [
      { value: threshold, label: `${threshold.toFixed(0)} °F threshold`, kind: "action" },
    ];
    const ceil = num(p.thresholds?.ceiling_f);
    if (ceil !== null) out.push({ value: ceil, label: `${ceil.toFixed(0)} °F ceiling`, kind: "ceiling" });
    return out;
  };
  // Hover is LOCAL, as on every fleet track; the ruler is navigation.
  let hoverIdx: number | null = null;
  temp = createTempChart(tempCanvas, {
    points,
    thresholds: rules(),
    todayIndex: tw.nowIndex,
    onHover: (_pt, i) => {
      hoverIdx = i;
      paintIntake();
    },
  });
  function paintIntake(): void {
    const ci = indexAtTime(tw.dates, getCursor());
    const live = hoverIdx ?? ci;
    const i = live ?? (tw.nowIndex >= 0 ? tw.nowIndex : null);
    const pt = i === null ? null : (points[i] ?? null);
    temp?.update({ cursorIndex: ci });
    renderReadout(tempB.readout, headToHeadReadout(pt, i, live !== null, colours));
  }
  paintIntake();
  teardowns.push(subscribeCursor(paintIntake));
  teardowns.push(bindMeter(
    tempB, tw.dates,
    points.map((pt) => pt.measured ?? pt.hindcast ?? pt.estimate ?? pt.forecast ?? null),
    "°F", 1, () => threshold, tw.nowIndex >= 0 ? tw.nowIndex : null,
  ));
  if (points.some((pt) => pt.measured !== null || pt.forecast !== null || pt.estimate !== null)) {
    drawn++;
  }

  // THEME CHANGES: colours are literals from the theme; re-resolve on change.
  const recolour = (): void => {
    Object.assign(colours, resolveColours(cssVar));
    colours.range = rgba(cssVar("--warn", "#b46a00"), 0.45);
    for (const s of stationCharts) s.chart.update({ series: s.rebuild() });
    temp?.update({});
    paintIntake();
  };
  const themeWatch = new MutationObserver(recolour);
  themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  teardowns.push(() => themeWatch.disconnect());

  ruler = createTimeRuler(rulerCanvas, {
    xLabels: tw.dates,
    gutterLeft: GUTTER,
    majorTick: dayTick,
  });
  transport.update(tw.dates);

  return {
    setThreshold(f: number): void {
      threshold = f;
      temp?.update({ thresholds: rules() });
    },
    destroy(): void {
      transport.destroy();
      ruler?.destroy();
      for (const off of teardowns) off();
      for (const b of blocks) (b.chart as Chart | null)?.destroy();
      temp?.destroy();
    },
    drawnSeries: () => drawn,
  };
}
